import { useCallback, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { useTranslation } from '@crehana/i18n';
import Dropdown from '@crehana/ui/dist/Dropdown';
import { Col, Container, Row } from '@crehana/ui/dist/GridV2';
import { CuiOptionType } from '@crehana/ui/dist/helpers/types';
import Loader from '@crehana/ui/dist/Loader';
import Pagination from '@crehana/ui/dist/Pagination';
import { notEmpty } from '@crehana/utils';

import DownloadableCard from '@/views/B2BLanding/components/DownloadableCard';

import { ORDER_BY } from '../../constants';
import GET_DOWNLOADABLE_RESOURCES from '../../graphql/GetResourceTypes.graphql';
import { GetResourceTypes } from '../../graphql/types/GetResourceTypes';
import useAvailableResourceCategories from '../../hooks/useAvailableResourceCategories';
import useDownloadableResources from '../../hooks/useDownloadableResources';
import localeEs from '../../i18n/localeEs';
import AsideNav from './components/AsideNav';
import CategoryList from './components/CategoryList';

const Body = () => {
  const client = useApolloClient();
  const { t } = useTranslation();

  const ORDEY_BY_OPTIONS = [
    {
      label: t('ORDER_ALL', { defaultValue: localeEs.es.ORDER_ALL }),
      value: ORDER_BY.ALL,
    },
    {
      label: t('ORDER_RECENT', { defaultValue: localeEs.es.ORDER_RECENT }),
      value: ORDER_BY.RECENT,
    },
    {
      label: t('ORDER_POPULARITY', {
        defaultValue: localeEs.es.ORDER_POPULARITY,
      }),
      value: ORDER_BY.LIKES,
    },
    {
      label: t('ORDER_OLDER', { defaultValue: localeEs.es.ORDER_OLDER }),
      value: ORDER_BY.OLDEST,
    },
  ];

  const response = client.cache.readQuery<GetResourceTypes>({
    query: GET_DOWNLOADABLE_RESOURCES,
  });
  const recourceTypes = [
    t('ALL_DOWNLOADABLE', { defaultValue: localeEs.es.ALL_DOWNLOADABLE }),
    ...(response?.typesResources?.filter(notEmpty) || []),
  ];
  const resourceTypeOptions = recourceTypes.map(el => ({
    value: el,
    label: el,
  }));
  const [selectedTypeResource, setSelectedTypeResource] = useState<string>(
    recourceTypes[0],
  );
  const [selectedOrderBy, setSelectedOrderBy] = useState<
    CuiOptionType<ORDER_BY>
  >(ORDEY_BY_OPTIONS[1]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // On change selected resource type, reset upper filters
  useEffect(() => {
    setSelectedCategories([]);
    setSelectedOrderBy(ORDEY_BY_OPTIONS[1]);
  }, [selectedTypeResource]);

  const { downloadables, currentPage, setCurrentPage, totalPages, loading } =
    useDownloadableResources({
      tags: selectedCategories.filter(notEmpty).join(','),
      orderBy: selectedOrderBy.value,
      typeResource: selectedTypeResource,
    });
  const { categories } = useAvailableResourceCategories({
    typeResource: selectedTypeResource,
  });

  const onSelect = useCallback(
    (id: string) => {
      setSelectedTypeResource(id);
    },
    [setSelectedTypeResource],
  );

  return (
    <Container className="my-32 md:my-64">
      <Row>
        <Col sm={3}>
          <AsideNav
            recourceTypes={recourceTypes}
            onSelect={onSelect}
            selectedTypeResource={selectedTypeResource}
            className="hidden sm:flex"
          />
          <Dropdown
            options={resourceTypeOptions}
            label={t('SELECT_DOWNLOADABLE', {
              defaultValue: localeEs.es.SELECT_DOWNLOADABLE,
            })}
            selected={{
              value: selectedTypeResource,
              label: selectedTypeResource,
            }}
            onSelected={selected => {
              if (selected) {
                onSelect(selected.value);
              }
            }}
            full
            className="block sm:hidden mb-16"
          />
        </Col>
        <Col sm={9}>
          <Row className="mb-24 sm:mb-56 flex-col-reverse sm:flex-row">
            <Col sm={8}>
              <CategoryList
                categories={categories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
              />
            </Col>
            <Col sm={4}>
              <Dropdown
                options={ORDEY_BY_OPTIONS}
                label={t('SORT_BY', {
                  defaultValue: localeEs.es.SORT_BY,
                })}
                selected={selectedOrderBy}
                onSelected={selected => {
                  if (selected) {
                    setSelectedOrderBy(selected);
                  }
                }}
                full
                className="mb-24 sm:mb-0"
              />
            </Col>
          </Row>
          {loading ? (
            <Row>
              <Col className="my-48 flex justify-center">
                <Loader />
              </Col>
            </Row>
          ) : (
            <Row>
              {downloadables.length > 0 ? (
                downloadables.map(downloadable => (
                  <Col
                    key={downloadable.originalId}
                    sm={6}
                    lg={4}
                    className="mb-24 sm:mb-0"
                  >
                    <DownloadableCard downloadable={downloadable} />
                  </Col>
                ))
              ) : (
                <Col className="my-48 flex justify-center">
                  <p className="font-subtitle3">
                    {t('NO_RESULTS_FOUND', {
                      defaultValue: localeEs.es.NO_RESULTS_FOUND,
                    })}
                  </p>
                </Col>
              )}
            </Row>
          )}
          <Row className="flex justify-end pt-48">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default Body;
